import { booleanAttribute } from "#ce/utils/props";

// We had bugs in the past, where the props were mutated. Apply Object.freeze() to prevent this.
//
// `value` is absent on purpose and is documented in the story: the element owns a live `value`
// property, which a declared prop would shadow with an accessor onto the prop store.
export const props = Object.freeze({
  /**
   * Creates a sp-icon component before default content in the prepend slot.
   *
   * @type {String}
   * @default undefined
   */
  prependIcon: {
    type: String,
    default: undefined,
  },
  /**
   * Creates a sp-icon component after default content in the append slot.
   *
   * @type {String}
   * @default undefined
   */
  appendIcon: {
    type: String,
    default: undefined,
  },
  /**
   * Whether to propagate the click event on the append slot.
   *
   * @type {Boolean|String}
   * @default false
   */
  propagateAppendClick: booleanAttribute(),
  /**
   * Removes the ability to click or target the input.
   *
   * @type {Boolean|String}
   * @default false
   */
  disabled: booleanAttribute(),
  /**
   * Removes the ability to edit the input’s value.
   *
   * @type {Boolean|String}
   * @default false
   */
  readonly: booleanAttribute(),
  /**
   * Sets the input’s required attribute.
   *
   * @type {Boolean|String}
   * @default false
   */
  required: booleanAttribute(),
  /**
   * The message to use for validity errors.
   *
   * @type {String}
   * @default undefined
   */
  customValidity: {
    type: String,
    default: undefined,
  },
  /**
   * Sets the input’s inputmode attribute.
   *
   * @type {String}
   * @default undefined
   */
  inputmode: {
    type: String,
    default: undefined,
    validator: (value) => {
      const validValues = ["none", "text", "decimal", "numeric", "tel", "search", "email", "url"];
      return validValues.includes(value);
    },
  },
  /**
   * name attribute used for the element's form value — required to participate in a native form submit.
   *
   * @type {String}
   * @default undefined
   */
  name: {
    type: String,
    default: undefined,
  },
  /**
   * Sets the input’s placeholder text.
   *
   * @type {String}
   * @default undefined
   */
  placeholder: {
    type: String,
    default: undefined,
  },
  /**
   * Sets the input’s type attribute.
   *
   * @type {String}
   * @default "text"
   */
  type: {
    type: String,
    default: "text",
  },
  /**
   * Sets the input’s loading state.
   * If true, shows a linear progress bar.
   *
   * @type {Boolean|String}
   * @default false
   */
  loading: booleanAttribute(),
  /**
   * Allows for the component to be cleared.
   *
   * @type {Boolean|String}
   * @default false
   */
  clearable: booleanAttribute(),
  /**
   * Applied when using clearable and the input is dirty.
   *
   * @type {String}
   * @default "close"
   */
  clearIcon: {
    type: String,
    default: "close-filled",
  },
  clearIconFill: {
    type: String,
    default: "currentColor",
  },
  /**
   * Always show the clearable icon when the input is dirty (By default it only shows on hover).
   *
   * @type {Boolean|String}
   * @default false
   */
  persistentClear: booleanAttribute(),
  /**
   * Sets a `data-testid` on the inner `<input>` element.
   *
   * @type {String}
   * @default undefined
   */
  inputTestId: {
    type: String,
    default: undefined,
  },
  density: {
    type: String,
    validator: (value) => ["default", "compact", "comfortable"].includes(value),
    default: "default",
  },
  variant: {
    type: String,
    validator: (value) => ["default", "outlined", "plain"].includes(value),
    default: "default",
  },
});
